import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/shared/components/ui/button';
import { ChevronDown, Loader2 } from 'lucide-react';
import { EmptyState } from '../../analytics/shared/EmptyState';
import { EngagementLogFeedbackPanel } from './EngagementLogFeedbackPanel';
import type { EngagementLogRow } from '../../../types/engagement-metrics.types';

// Feature 102 — which rows can carry referral/call-request feedback.
const REFERRAL_SOURCE_TYPES = [
  'PROVIDER_REFERRAL',
  'SP_PROVIDER_REFERRAL',
  'MANUAL_PROFESSIONAL_REFERRAL',
];
const FEEDBACK_SOURCE_TYPES = [...REFERRAL_SOURCE_TYPES, 'CONSULTATION_REQUEST', 'MESSAGE'];
// `engagementType` on a log row is the backend's reportingType.
const FEEDBACK_REPORTING_TYPES = ['REFERRAL', 'FIRST_MESSAGE', 'CONSULTATION_REQUEST'];

const isFeedbackEligible = (row: EngagementLogRow): boolean =>
  row.sourceRecordId != null &&
  row.sourceRecordType != null &&
  FEEDBACK_SOURCE_TYPES.includes(row.sourceRecordType) &&
  (FEEDBACK_REPORTING_TYPES.includes(row.engagementType) ||
    REFERRAL_SOURCE_TYPES.includes(row.sourceRecordType));

const ROLE_BADGE: Record<string, { text: string; bg: string; label: string }> = {
  SERVICE_PROVIDER: { text: '#2c5c47', bg: '#e4ede8', label: 'Provider' },
  FINANCIAL_ADVISOR: { text: '#2c6e7d', bg: '#e2edf0', label: 'FP' },
  ADMIN: { text: '#87702f', bg: '#f3edd9', label: 'Admin' },
  CARE_SEEKER: { text: '#46584f', bg: '#edeae3', label: 'Care Seeker' },
  GUEST: { text: '#7f8a83', bg: '#f0ede6', label: 'Guest' },
};

const RoleBadge = ({ role }: { role: string }) => {
  const badge = ROLE_BADGE[role] ?? { text: '#7f8a83', bg: '#f0ede6', label: role };
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[.3px]"
      style={{ color: badge.text, backgroundColor: badge.bg }}
    >
      {badge.label}
    </span>
  );
};

const BillableTag = ({ isBillable }: { isBillable: boolean }) => (
  <span
    className="inline-flex items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold leading-none"
    style={
      isBillable
        ? { color: '#2c6a4a', backgroundColor: '#e3ede7' }
        : { color: '#7f8a83', backgroundColor: '#f0ede6' }
    }
  >
    {isBillable ? 'Billable' : 'Non-billable'}
  </span>
);

/** Canonical example from the design spec: a converted `PROFILE_VIEW` row shows this badge. */
const ConvertedBadge = () => (
  <span
    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold"
    style={{ color: '#2c6a4a', backgroundColor: '#e3ede7' }}
  >
    ✓ Converted to billable
  </span>
);

/** Shared resources only: whether the recipient has opened the share link yet. */
const ViewedTag = ({ viewed }: { viewed: boolean }) => (
  <span
    className="inline-flex items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold leading-none"
    style={
      viewed
        ? { color: '#2c6a4a', backgroundColor: '#e3ede7' }
        : { color: '#7f8a83', backgroundColor: '#f0ede6' }
    }
  >
    {viewed ? 'Viewed' : 'Not viewed'}
  </span>
);

const DateCell = ({ timestamp }: { timestamp: string }) => {
  const date = new Date(timestamp);
  return (
    <div>
      <p className="text-[12.5px] font-semibold" style={{ color: '#1b2a23' }}>
        {formatDistanceToNow(date, { addSuffix: true })}
      </p>
      <p className="text-[11px]" style={{ color: '#7f8a83' }}>
        {date.toLocaleDateString()}
      </p>
    </div>
  );
};

type EngagementLogTableProps = {
  rows: EngagementLogRow[];
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
};

const LOG_HEADERS = [
  'Engagement',
  'Initiator',
  'Provider',
  'Category / Specialty',
  'Region',
  'Billable',
  'Date',
  '', // feedback chevron column
];

export const EngagementLogTable = ({
  rows,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: EngagementLogTableProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = (rowId: string) =>
    setExpandedId((current) => (current === rowId ? null : rowId));

  if (!isLoading && rows.length === 0) {
    return <EmptyState message="No engagements match the selected filters." />;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead>
            <tr className="border-b" style={{ backgroundColor: '#faf8f4', borderColor: '#edeae3' }}>
              {LOG_HEADERS.map((header, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[.3px]"
                  style={{ color: '#7f8a83' }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const eligible = isFeedbackEligible(row);
              const isExpanded = eligible && expandedId === row.id;
              return [
              <tr
                key={row.id}
                className={isExpanded ? 'bg-[#faf8f4]' : 'border-b last:border-0'}
                style={{ borderColor: '#edeae3' }}
              >
                <td className="px-4 py-3 align-top">
                  <p className="text-[13px] font-semibold" style={{ color: '#1b2a23' }}>
                    {row.engagementTypeLabel}
                  </p>
                  <p className="text-[11px]" style={{ color: '#7f8a83' }}>
                    {row.id}
                  </p>
                  {row.convertedToBillable && (
                    <div className="mt-1">
                      <ConvertedBadge />
                    </div>
                  )}
                  {row.viewed != null && (
                    <div className="mt-1">
                      <ViewedTag viewed={row.viewed} />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center gap-2">
                    <RoleBadge role={row.initiatorRole} />
                  </div>
                  <p className="text-[12.5px] mt-1" style={{ color: '#1b2a23' }}>
                    {row.initiatorName ?? '—'}
                  </p>
                  {row.initiatorEmail && (
                    <p className="text-[11px]" style={{ color: '#7f8a83' }}>
                      {row.initiatorEmail}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <p className="text-[12.5px] font-semibold" style={{ color: '#1b2a23' }}>
                    {row.providerName ?? '—'}
                  </p>
                  <p className="text-[11px]" style={{ color: '#7f8a83' }}>
                    {row.companyName ?? 'Independent provider'}
                  </p>
                </td>
                <td className="px-4 py-3 align-top text-[12.5px]" style={{ color: '#46584f' }}>
                  {[...row.categoryNames, ...row.specialtyNames].join(', ') || 'No filter selected'}
                </td>
                <td className="px-4 py-3 align-top text-[12.5px]" style={{ color: '#46584f' }}>
                  {row.stateCode ?? '—'}
                </td>
                <td className="px-4 py-3 align-top">
                  <BillableTag isBillable={row.isBillable} />
                </td>
                <td className="px-4 py-3 align-top">
                  <DateCell timestamp={row.timestamp} />
                </td>
                <td className="px-4 py-3 align-top text-right">
                  {eligible && (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} feedback for engagement ${row.id}`}
                      className="inline-flex items-center gap-1 rounded p-1 text-[11px] font-bold uppercase tracking-[.3px] hover:bg-[#edeae3]"
                      style={{ color: '#7f8a83' }}
                      onClick={() => toggle(row.id)}
                      data-testid={`engagement-feedback-toggle-${row.id}`}
                    >
                      Feedback
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                </td>
              </tr>,
              isExpanded && (
                <tr
                  key={`${row.id}-feedback`}
                  className="border-b last:border-0"
                  style={{ borderColor: '#edeae3', backgroundColor: '#faf8f4' }}
                >
                  <td colSpan={LOG_HEADERS.length} className="px-4 pb-3">
                    <EngagementLogFeedbackPanel
                      subjectType={row.sourceRecordType as string}
                      subjectId={row.sourceRecordId as string}
                    />
                  </td>
                </tr>
              ),
            ];
            })}
          </tbody>
        </table>
      </div>

      {hasNextPage && (
        <div className="flex justify-center py-3 border-t" style={{ borderColor: '#edeae3' }}>
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
